import { UserAppointmentsDto } from "@/models/club";
import { ClickEventSlotProps } from "@/pages/dashboard/calendar/type";
import moment from "moment-jalaali";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { EventItem } from "./EventItem";
import { BLOCKS_PER_DAY, TIME_BLOCKS, computeEventColumns, getEventBlocks, getOffShiftRanges } from "./helper/timeGrid";
import { CalendarEvent } from "./types";

export type TimeGridColumn = {
  key: string;
  partner: UserAppointmentsDto;
  date: moment.Moment;
  isToday: boolean;
  events: CalendarEvent[];
  shiftsByHour: boolean[];
};

type TimeGridProps = {
  columns: TimeGridColumn[];
  headers: React.ReactNode[];
  // Changing this key re-runs the initial scroll (to now when today is visible, else 09:00).
  scrollKey: string;
  tintToday?: boolean;
  onEmptySlotClick?: (props: ClickEventSlotProps) => void;
  onEventClick?: (event: CalendarEvent) => void;
  setSelectedEvent?: (event: CalendarEvent | null) => void;
};

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
const minutesNow = () => { const now = new Date(); return now.getHours() * 60 + now.getMinutes(); };

// Re-renders only the component using it, once per minute.
const useNowMinutes = () => {
  const [minutes, setMinutes] = useState(minutesNow);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => { setMinutes(minutesNow()); timer = setTimeout(tick, 60000 - (Date.now() % 60000)); };
    timer = setTimeout(tick, 60000 - (Date.now() % 60000));
    return () => clearTimeout(timer);
  }, []);
  return minutes;
};

const NowLine = () => {
  const minutes = useNowMinutes();
  return <div className="tg-now" style={{ top: `calc(var(--slot-h) * ${minutes / 10})` }} aria-hidden />;
};

const NowLabel = () => {
  const minutes = useNowMinutes();
  const label = `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
  return <span className="tg-now-label" style={{ top: `calc(var(--slot-h) * ${minutes / 10})` }}>{label}</span>;
};

const Gutter = React.memo(function Gutter({ showNow }: { showNow: boolean }) {
  return (
    <div className="tg-gutter" aria-hidden>
      {HOURS.map(hour => <span key={hour} className="tg-hour" style={{ top: `calc(var(--hour-h) * ${hour})` }}>{String(hour).padStart(2, "0")}:00</span>)}
      {showNow && <NowLabel />}
    </div>
  );
});

type ColumnProps = Pick<TimeGridProps, "onEventClick" | "setSelectedEvent"> & { column: TimeGridColumn; index: number; tintToday?: boolean };

const Column = React.memo(function Column({ column, index, tintToday, onEventClick, setSelectedEvent }: ColumnProps) {
  const layout = useMemo(() => computeEventColumns(column.events), [column.events]);
  const offRanges = useMemo(() => getOffShiftRanges(column.shiftsByHour), [column.shiftsByHour]);
  return (
    <div className="tg-col" data-col={index} data-today={(tintToday && column.isToday) || undefined}>
      {offRanges.map(([start, end]) => <div key={start} className="tg-off" style={{ top: `calc(var(--hour-h) * ${start})`, height: `calc(var(--hour-h) * ${end - start})` }} />)}
      {column.events.map((event, eventIndex) => {
        const { start, span } = getEventBlocks(event);
        const position = event.id != null ? layout.get(event.id) : undefined;
        const total = position && position.totalColumns > 1 ? position.totalColumns : 1;
        return (
          <EventItem key={`${event.id}-${eventIndex}`} view="grid" event={event} onEventClick={onEventClick} setSelectedEvent={setSelectedEvent}
            style={{
              top: `calc(var(--slot-h) * ${start})`,
              height: `calc(var(--slot-h) * ${span})`,
              insetInlineStart: `${((position?.columnIndex ?? 0) * 100) / total}%`,
              width: `${100 / total}%`,
            }} />
        );
      })}
      {column.isToday && <NowLine />}
    </div>
  );
});

function TimeGridComponent({ columns, headers, scrollKey, tintToday, onEmptySlotClick, onEventClick, setSelectedEvent }: TimeGridProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const ghostRef = useRef<HTMLDivElement>(null);
  const centerRatio = useRef<number | null>(null);
  const anyToday = columns.some(column => column.isToday);

  const blockFromPointer = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.closest(".calendar-event")) return null;
    const columnEl = target.closest<HTMLElement>("[data-col]");
    if (!columnEl) return null;
    const rect = columnEl.getBoundingClientRect();
    const slot = rect.height / BLOCKS_PER_DAY;
    const block = Math.min(BLOCKS_PER_DAY - 1, Math.max(0, Math.floor((event.clientY - rect.top) / slot)));
    return { columnEl, block, slot };
  };

  const handleClick = (event: React.MouseEvent) => {
    const hit = blockFromPointer(event);
    if (!hit) return;
    const column = columns[Number(hit.columnEl.dataset.col)];
    if (column) onEmptySlotClick?.({ partner: column.partner, hour: TIME_BLOCKS[hit.block].timeString, date: column.date.clone() });
  };

  // Hover preview of the ten-minute slot; written straight to the DOM so moving the mouse never re-renders React.
  const handleMove = (event: React.MouseEvent) => {
    const ghost = ghostRef.current;
    if (!ghost) return;
    const hit = blockFromPointer(event);
    if (!hit) { ghost.style.opacity = "0"; return; }
    ghost.style.opacity = "1";
    ghost.style.transform = `translate(${hit.columnEl.offsetLeft}px, ${hit.block * hit.slot}px)`;
    ghost.style.width = `${hit.columnEl.offsetWidth}px`;
    ghost.style.height = `${hit.slot}px`;
    ghost.textContent = TIME_BLOCKS[hit.block].timeString;
  };
  const hideGhost = () => { if (ghostRef.current) ghostRef.current.style.opacity = "0"; };

  const rememberCenter = useCallback(() => {
    const scroller = scrollerRef.current, body = bodyRef.current;
    if (scroller && body && body.offsetHeight) centerRatio.current = (scroller.scrollTop + scroller.clientHeight / 2) / body.offsetHeight;
  }, []);

  // Initial position: current time when today is visible, otherwise 09:00 (same targets as before), centered.
  useLayoutEffect(() => {
    const scroller = scrollerRef.current, body = bodyRef.current;
    if (!scroller || !body) return;
    const minutes = anyToday ? minutesNow() : 9 * 60;
    scroller.scrollTop = Math.max(0, (minutes / 1440) * body.offsetHeight - scroller.clientHeight / 2 + body.offsetTop);
    rememberCenter();
  }, [scrollKey, anyToday, rememberCenter]);

  // Keep the same time centered when the density (row height) or viewport changes.
  useEffect(() => {
    const scroller = scrollerRef.current, body = bodyRef.current;
    if (!scroller || !body || typeof ResizeObserver === "undefined") return;
    let height = body.offsetHeight;
    const observer = new ResizeObserver(() => {
      if (body.offsetHeight === height) return;
      height = body.offsetHeight;
      if (centerRatio.current != null) scroller.scrollTop = Math.max(0, centerRatio.current * height - scroller.clientHeight / 2);
    });
    observer.observe(body);
    return () => observer.disconnect();
  }, []);

  const count = Math.max(columns.length, 1);
  return (
    <div className="tg-scroll calendar-scroll" ref={scrollerRef} onScroll={rememberCenter}>
      <div className="tg-inner" style={{ "--tg-cols": count } as React.CSSProperties}>
        <div className="tg-head">
          <div className="tg-corner" />
          {headers.map((header, index) => <div key={columns[index]?.key ?? index} className="tg-head-cell" data-today={(tintToday && columns[index]?.isToday) || undefined}>{header}</div>)}
        </div>
        <div className="tg-body" ref={bodyRef} onClick={handleClick} onMouseMove={handleMove} onMouseLeave={hideGhost}>
          <Gutter showNow={anyToday} />
          {columns.map((column, index) => <Column key={column.key} column={column} index={index} tintToday={tintToday} onEventClick={onEventClick} setSelectedEvent={setSelectedEvent} />)}
          <div className="tg-ghost" ref={ghostRef} aria-hidden />
        </div>
      </div>
    </div>
  );
}

export const TimeGrid = React.memo(TimeGridComponent);
